import { Injectable } from "@angular/core";
import { catchError, Observable, of, ReplaySubject, tap } from "rxjs";
import { environment } from "@env/environment";
import { HttpClient, HttpParams } from "@angular/common/http";
import { TrafficAgent, AdminTrafficAgentCreationRequestDTO, TrafficAgentRequest } from "./traffic-agent.interface";

@Injectable()
export class TrafficAgentService {

    baseUrl = environment.administration_url + '/admin/traffic-agents';
    appUrl = environment.administration_url + '/app/traffic-agents';
    private _trafficAgent: ReplaySubject<TrafficAgent> = new ReplaySubject<TrafficAgent>(1);
    private _trafficAgents: ReplaySubject<TrafficAgent[]> = new ReplaySubject<TrafficAgent[]>(1);
    private _metadata: ReplaySubject<any> = new ReplaySubject<any>(1);

    set trafficAgent(value: TrafficAgent) {
        this._trafficAgent.next(value);
    }

    get trafficAgent$() {
        return this._trafficAgent.asObservable();
    }

    set trafficAgents(value: TrafficAgent[]) {
        this._trafficAgents.next(value);
    }

    get trafficAgents$() {
        return this._trafficAgents.asObservable();
    }

    set metadata(value: any) {
        this._metadata.next(value);
    }

    get metadata$() {
        return this._metadata.asObservable();
    }

    constructor(
        private _httpClient: HttpClient
    ) { }

    getAll(filters?: { page?: number; size?: number; keyword?: string }): Observable<any> {
        let params = new HttpParams();
        if (filters) {
            if (filters.page !== undefined) params = params.set('page', filters.page.toString());
            if (filters.size !== undefined) params = params.set('size', filters.size.toString());
            if (filters.keyword) params = params.set('keyword', filters.keyword);
        }
        return this._httpClient.get<any>(`${this.baseUrl}`, { params })
        .pipe(
            tap((response: any) => {
                this.trafficAgents = response?.content || [];
                this.metadata = response;
                return response;
            }),
            catchError(() => of({ content: [] }))
        );
    }

    getById(uuid: string): Observable<TrafficAgent> {
        return this._httpClient.get<TrafficAgent>(`${this.appUrl}/${uuid}`)
        .pipe(
            tap((agent) => {
                this.trafficAgent = agent;
                return agent;
            }),
            catchError(() => of({} as TrafficAgent))
        );
    }

    createWithUser(dto: AdminTrafficAgentCreationRequestDTO): Observable<TrafficAgent> {
        return this._httpClient.post<TrafficAgent>(`${this.baseUrl}-and-users`, dto)
        .pipe(
            tap((agent) => {
                this.trafficAgent = agent;
                return agent;
            })
        );
    }

    update(uuid: string, data: TrafficAgentRequest): Observable<TrafficAgent> {
        return this._httpClient.put<TrafficAgent>(`${this.appUrl}/${uuid}`, data)
        .pipe(
            tap((agent) => {
                this.trafficAgent = agent;
                return agent;
            })
        );
    }

    activate(uuid: string): Observable<TrafficAgent> {
        return this._httpClient.patch<TrafficAgent>(`${this.appUrl}/${uuid}/activate`, {})
        .pipe(
            tap((agent) => {
                this.trafficAgent = agent;
                return agent;
            }),
            catchError(() => of({} as TrafficAgent))
        );
    }

    deactivate(uuid: string): Observable<TrafficAgent> {
        return this._httpClient.patch<TrafficAgent>(`${this.appUrl}/${uuid}/deactivate`, {})
        .pipe(
            tap((agent) => {
                this.trafficAgent = agent;
                return agent;
            }),
            catchError(() => of({} as TrafficAgent))
        );
    }
}
